import { Link } from "react-router-dom";
import { useLanguage } from "../i18n/LanguageContext";
import { BookOpen, MessageCircle, Mail, MapPin, ArrowUpLeft, ArrowUpRight } from "lucide-react";

const LOGO_URL = "/icons/mart-logo-full.jpg";
const WHATSAPP_NUMBER = "972528614433";
const CONTACT_EMAIL = "mart@mart-mineral.com";
const PICKUP_MAP_URL = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent("רחוב יהודה מכבי, תל אביב")}`;

export const Footer = () => {
  const { t, language } = useLanguage();
  const isRTL = language === "he";
  const Arrow = isRTL ? ArrowUpLeft : ArrowUpRight;

  const whatsappUrl = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(
    isRTL ? "היי, יש לי שאלה לגבי M'ART" : "Hi, I have a question about M'ART"
  )}`;

  const blocks = [
    {
      testid: "footer-pickup-btn",
      href: PICKUP_MAP_URL,
      external: true,
      icon: MapPin,
      title: isRTL ? "נק׳ איסוף עצמי" : "Self Pickup",
      detail: isRTL ? "רחוב יהודה מכבי, תל אביב" : "Yehuda Maccabi St, Tel Aviv",
      sub: isRTL ? "פתחו במפה · בתיאום מראש" : "Open in maps · by arrangement",
      ltr: false,
      hover: "group-hover:border-[#C47F64] group-hover:text-[#C47F64]",
    },
    {
      testid: "footer-whatsapp-btn",
      href: whatsappUrl,
      external: true,
      icon: MessageCircle,
      title: "WhatsApp",
      detail: isRTL ? "לשאלות והתייעצות" : "Questions & advice",
      sub: isRTL ? "התכתבו איתנו" : "Chat with us",
      ltr: false,
      hover: "group-hover:border-[#25D366] group-hover:text-[#25D366]",
    },
    {
      testid: "footer-email-btn",
      href: `mailto:${CONTACT_EMAIL}`,
      external: false,
      icon: Mail,
      title: isRTL ? "אימייל" : "Email",
      detail: CONTACT_EMAIL,
      sub: isRTL ? "כתבו לנו" : "Write to us",
      ltr: true,
      hover: "group-hover:border-[#4A5D43] group-hover:text-[#4A5D43]",
    },
  ];

  return (
    <footer
      data-testid="footer"
      className="relative bg-[#FAF8F5] pt-10 sm:pt-16 pb-8 border-t border-[#E8E4DC] overflow-hidden"
      dir={isRTL ? "rtl" : "ltr"}
    >
      <div className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-[#C47F64]/50 to-transparent" />

      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        {/* Hero row */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-5 sm:gap-8 mb-7 sm:mb-12">
          <div className="text-center md:text-start">
            <img
              src={LOGO_URL}
              alt="M'art"
              className="h-9 w-auto object-contain mx-auto md:mx-0 mb-3"
              style={{ mixBlendMode: "multiply" }}
            />
            <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl text-[#2C3329] leading-[1.15] tracking-tight">
              {isRTL ? "המדע, הטיפים והמתכונים" : "The science, tips & recipes"}
              <br />
              <span className="text-[#C47F64]">{isRTL ? "מחכים לכם במאמרים." : "are waiting in our articles."}</span>
            </h2>
          </div>

          <Link
            to="/articles"
            data-testid="footer-articles-btn"
            className="group inline-flex items-center justify-center gap-3 px-7 py-3.5 bg-[#C47F64] text-white rounded-full shadow-lg hover:shadow-xl hover:bg-[#4A5D43] transition-all duration-300 shrink-0 self-center"
          >
            <BookOpen className="w-5 h-5 group-hover:rotate-[-10deg] transition-transform duration-300" />
            <span className="text-sm font-semibold tracking-wide">
              {isRTL ? "קראו את המאמרים" : "Read the Articles"}
            </span>
          </Link>
        </div>

        {/* Architectural contact grid (compact rows on mobile, cards on desktop) */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-[#E8E4DC] border border-[#E8E4DC] rounded-2xl overflow-hidden mb-7 sm:mb-12 shadow-sm">
          {blocks.map((b) => {
            const Icon = b.icon;
            return (
              <a
                key={b.testid}
                href={b.href}
                data-testid={b.testid}
                {...(b.external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
                className="group relative bg-[#FAF8F5] hover:bg-white active:bg-white px-5 py-4 md:p-8 flex flex-row md:flex-col items-center md:items-start gap-4 md:gap-0 transition-colors duration-300 cursor-pointer"
              >
                <div
                  className={`w-11 h-11 md:w-12 md:h-12 rounded-full border border-[#D1CDC5] flex items-center justify-center text-[#2C3329] shrink-0 mb-0 md:mb-5 transition-colors duration-300 ${b.hover}`}
                >
                  <Icon className="h-5 w-5" strokeWidth={1.5} />
                </div>

                <div className="flex-1 min-w-0 flex flex-col items-start">
                  <span className="text-[10px] md:text-[11px] uppercase tracking-[0.18em] text-[#9A9790] mb-0.5 md:mb-1.5">
                    {b.title}
                  </span>
                  <span
                    className="font-serif text-base md:text-lg text-[#2C3329] leading-snug truncate max-w-full"
                    dir={b.ltr ? "ltr" : undefined}
                  >
                    {b.detail}
                  </span>
                  <span className="text-xs text-[#C47F64] md:text-[#6B7266] mt-1 md:mt-2 md:pt-2 font-semibold md:font-medium">
                    {b.sub}
                  </span>
                </div>

                {/* Arrow: persistent tap-cue on mobile, hover-reveal corner on desktop */}
                <Arrow
                  className={`h-5 w-5 shrink-0 text-[#C47F64] md:text-[#C4C0B6] md:absolute md:top-5 ${isRTL ? "md:left-5" : "md:right-5"} md:opacity-0 md:-translate-y-1 md:group-hover:opacity-100 md:group-hover:translate-y-0 transition-all duration-300`}
                />
              </a>
            );
          })}
        </div>

        {/* Divider */}
        <div className="w-12 h-px bg-gradient-to-r from-[#C47F64] to-[#4A5D43] mx-auto mb-4" />

        {/* Disclaimer */}
        <p className="text-[11px] sm:text-xs text-[#6B7266] text-center max-w-md mx-auto leading-relaxed mb-2">
          {t.footer.disclaimer}
        </p>

        {/* Copyright */}
        <p className="text-[11px] sm:text-xs text-[#9A9790] text-center">{t.footer.copyright}</p>
      </div>
    </footer>
  );
};

export default Footer;
